import Component from '../Component';

export default class Popup extends Component {
  constructor(theme, element) {
    super(theme, element);

    this.STORAGE_KEY = 'wetheme-popup';
    this.VISIBILITY_KEY = 'popup-visible';
    this.isFullscreen = this.element.dataset.desktopType === 'fullscreen';
    this.popupSkipDays = parseInt(this.element.dataset.popupTime, 10);
    this.popupDelay = parseInt(this.element.dataset.popupDelay, 10);
    this.testMode = this.element.dataset.popupTestMode === 'true';
    this.hasLocalStorage = this._isLocalStorageSupported();

    // Cache DOM elements to avoid multiple queries
    this.closeButton = this.element.querySelector('.popup--close-btn');
    this.fullscreenCloseButton = this.element.querySelector('.popup--close');
    this.slideUpElement = document.querySelector('#slide-up');
    this.fullscreenElement = document.querySelector('#fullscreen');
    this.popupInput = this.element.querySelector('input[type="email"]');

    // Event bindings
    this.closeButton?.addEventListener('click', this.closePopup);
    this.fullscreenCloseButton?.addEventListener('click', this.closePopup);
    window.addEventListener('resize', this.onWindowResize);

    this.initPopup();
    this.focusTimeout = null;
  }

  initPopup = () => {
    if (this.shouldShowPopup()) {
      const delay = this.testMode ? 0 : 1000 * this.popupDelay;
      setTimeout(() => {
        this.setExpiry(this.popupSkipDays);
        this.show();
      }, delay);
    }
  }

  shouldShowPopup = () => {
    if (this.testMode) return true;
    if (this.hasLocalStorage && localStorage.getItem(this.VISIBILITY_KEY) === 'true') {
      return true;
    } else if (this.isExpiredOrNotSet()) {
      return true;
    }
    return false;
  }

  isExpiredOrNotSet = () => {
    if (!this.hasLocalStorage) return true;

    const popupInfo = localStorage.getItem(this.STORAGE_KEY);
    if (popupInfo) {
      try {
        const expiry = Date.parse(JSON.parse(popupInfo).expiry);
        return expiry <= Date.now();
      } catch (e) {
        console.warn('Unable to read popup expiry date', e);
      }
    }
    return true;
  }

  setExpiry = (days) => {
    if (this.hasLocalStorage) {
      try {
        const expiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify({ expiry: expiry.toISOString() }));
      } catch (e) {
        console.warn('Unable to set popup expiry:', e);
      }
    }
  }

  onWindowResize = () => {
    if (this.shouldShowPopup()) {
      this.show();
    }
  }

  isMobileView = () => {
    return window.innerWidth < 769;
  }

  show = () => {
    this.closePopup(); // Ensure the previous popup state is cleaned up
    if (this.isMobileView() || !this.isFullscreen) {
      this.showSlideUp();
    } else {
      this.showFullscreen();
    }
    this.updateVisibilityStorage(true);
    // Focus the close button after a short delay
    this.focusTimeout = setTimeout(() => {
      this.popupInput?.focus();
    }, 300);
  }

  showSlideUp = () => {
    if (this.slideUpElement) {
      this.slideUpElement.style.visibility = 'visible';
    }
    this.element.classList.add('slide-up', 'visible');
  }

  showFullscreen = () => {
    this.element.classList.add('fullscreen', 'visible');
    if (this.fullscreenElement) {
      this.fullscreenElement.style.display = 'flex';
      document.body.classList.add('fullscreen-popup-open');
      document.querySelector('#PopupOverlay')?.addEventListener('click', this.closePopup);
    }
  }

  closePopup = () => {
    this.element.classList.remove('visible', 'fullscreen', 'slide-up');
    if (this.fullscreenElement) {
      this.fullscreenElement.style.display = 'none';
      document.body.classList.remove('fullscreen-popup-open');
      document.querySelector('#PopupOverlay')?.removeEventListener('click', this.closePopup);
    }
    if (this.slideUpElement) {
      this.slideUpElement.style.visibility = 'hidden';
    }
    this.updateVisibilityStorage(false);
    // Clear the focus timeout if it exists
    if (this.focusTimeout) {
      clearTimeout(this.focusTimeout);
      this.focusTimeout = null;
    }
  }

  updateVisibilityStorage = (visible) => {
    if (this.hasLocalStorage) {
      if (visible) {
        localStorage.setItem(this.VISIBILITY_KEY, 'true');
      } else {
        localStorage.removeItem(this.VISIBILITY_KEY);
      }
    }
  }

  _isLocalStorageSupported = () => {
    try {
      return typeof Storage !== 'undefined' && window.localStorage;
    } catch (e) {
      return false;
    }
  }
}
